import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CircleNotchIcon,
  UploadIcon,
  XIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import { useCreateDiscount } from '@/api/rpc/hooks/accounts-receivable/useCreateDiscount';
import type { CreateDiscountInput, DiscountType } from '@erp/rpc';

const ALLOWED_FILE_TYPES = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
];

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
];

interface CreateDiscountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  productVariantId: string;
  sku: string;
  poUnitPrice: number;
  listPrice: number | null;
}

const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
  { value: 'FIXED_PRICE', label: 'Fixed Price' },
  { value: 'PERCENT', label: 'Percentage Off' },
];

function generateDefaultName(customerName: string, sku: string): string {
  const truncatedCustomer =
    customerName.length > 30 ? customerName.slice(0, 30) + '...' : customerName;
  return `${truncatedCustomer} - ${sku} Discount`;
}

export function CreateDiscountModal({
  open,
  onOpenChange,
  customerId,
  customerName,
  productVariantId,
  sku,
  poUnitPrice,
  listPrice,
}: CreateDiscountModalProps) {
  const createDiscount = useCreateDiscount();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [name, setName] = useState(() =>
    generateDefaultName(customerName, sku),
  );
  const [discountType, setDiscountType] = useState<DiscountType>('FIXED_PRICE');
  const [value, setValue] = useState<string>(poUnitPrice.toFixed(2));
  const [effectiveStart, setEffectiveStart] = useState<string>('');
  const [effectiveEnd, setEffectiveEnd] = useState<string>('');

  // Attachment state
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Validation
  const numericValue = parseFloat(value);
  const isValueValid = !isNaN(numericValue) && numericValue > 0;
  const isFormValid = name.trim().length > 0 && isValueValid && !isUploading;

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setUploadError(
        `Invalid file type. Allowed: ${ALLOWED_FILE_TYPES.join(', ')}`,
      );
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/uploads/file?folder=discounts', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const result = await response.json();
      setAttachmentUrl(result.url);
      setAttachmentName(file.name);
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Failed to upload file',
      );
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  function handleRemoveAttachment() {
    setAttachmentUrl(null);
    setAttachmentName(null);
    setUploadError(null);
  }

  function handleDiscountTypeChange(newType: DiscountType) {
    setDiscountType(newType);
    // When switching types, recalculate default value
    if (newType === 'FIXED_PRICE') {
      setValue(poUnitPrice.toFixed(2));
    } else if (newType === 'PERCENT' && listPrice && listPrice > 0) {
      const percentOff = ((listPrice - poUnitPrice) / listPrice) * 100;
      setValue(Math.max(0, percentOff).toFixed(2));
    }
  }

  async function handleSubmit() {
    if (!isFormValid) return;

    const input: CreateDiscountInput = {
      name: name.trim(),
      discountType,
      value: numericValue,
      effectiveStart: effectiveStart || null,
      effectiveEnd: effectiveEnd || null,
      scopes: [
        {
          customerId,
          targetType: 'SKU',
          targetId: productVariantId,
          attachment: attachmentUrl,
        },
      ],
    };

    try {
      await createDiscount.mutateAsync(input);
      onOpenChange(false);
    } catch {
      // Error is handled by the hook's onError callback
    }
  }

  function getValueLabel(): string {
    switch (discountType) {
      case 'FIXED_PRICE':
        return 'Price ($)';
      case 'PERCENT':
        return 'Discount (%)';
      default:
        return 'Value';
    }
  }

  function getValueHelperText(): string {
    switch (discountType) {
      case 'FIXED_PRICE':
        return `Customer will pay this price. PO price: $${poUnitPrice.toFixed(2)}`;
      case 'PERCENT':
        return listPrice
          ? `Percentage off list price ($${listPrice.toFixed(2)})`
          : 'Percentage off list price';
      default:
        return '';
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Create Discount</DialogTitle>
          <DialogDescription>
            Create a new discount for {sku} as applied to {customerName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Discount Name */}
          <div className="space-y-2">
            <Label htmlFor="discount-name">Discount Name</Label>
            <Input
              id="discount-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter discount name"
            />
          </div>

          {/* Discount Type */}
          <div className="space-y-2">
            <Label htmlFor="discount-type">Discount Type</Label>
            <Select
              value={discountType}
              onValueChange={(val) =>
                handleDiscountTypeChange(val as DiscountType)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select discount type" />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value */}
          <div className="space-y-2">
            <Label htmlFor="discount-value">{getValueLabel()}</Label>
            <Input
              id="discount-value"
              type="number"
              step="0.01"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter value"
            />
            <p className="text-xs text-muted-foreground">
              {getValueHelperText()}
            </p>
          </div>

          {/* Effective Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effective-start">Effective Start</Label>
              <Input
                id="effective-start"
                type="date"
                value={effectiveStart}
                onChange={(e) => setEffectiveStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="effective-end">Effective End</Label>
              <Input
                id="effective-end"
                type="date"
                value={effectiveEnd}
                onChange={(e) => setEffectiveEnd(e.target.value)}
              />
            </div>
          </div>

          {/* Attachment Upload */}
          <div className="space-y-2">
            <Label>Attachment</Label>
            {attachmentUrl && attachmentName ? (
              <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1 truncate">
                  {attachmentName}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={handleRemoveAttachment}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_FILE_TYPES.join(',')}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="attachment-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UploadIcon className="h-4 w-4 mr-2" />
                  )}
                  {isUploading ? 'Uploading...' : 'Upload File'}
                </Button>
              </div>
            )}
            {uploadError && (
              <p className="text-xs text-destructive">{uploadError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Allowed: PDF, images, Word, Excel, CSV (max 10MB)
            </p>
          </div>

          {/* Context info */}
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <div className="flex justify-between">
              <span>Customer:</span>
              <span className="font-medium text-foreground">
                {customerName}
              </span>
            </div>
            <div className="flex justify-between">
              <span>SKU:</span>
              <span className="font-mono text-foreground">{sku}</span>
            </div>
            <div className="flex justify-between">
              <span>PO Unit Price:</span>
              <span className="text-foreground">${poUnitPrice.toFixed(2)}</span>
            </div>
            {listPrice !== null && (
              <div className="flex justify-between">
                <span>List Price:</span>
                <span className="text-foreground">${listPrice.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createDiscount.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || createDiscount.isPending}
          >
            {createDiscount.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create Discount
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
